import { captureException } from '@sentry/react'
import * as v from 'valibot'
import { type GenericSchema, flatten, safeParse } from 'valibot'

import { env } from '@/env'

import { SENTRY_SECTIONS } from '../constants/constants'

const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2) ?? 'Unable to serialize value'
  } catch {
    return 'Unable to serialize value'
  }
}

/**
 * Validates data against a valibot schema and returns type-safe data
 *
 * This function performs schema validation using Valibot's safeParse method.
 * If validation fails, it logs the error details and captures the exception
 * in Sentry for monitoring purposes. In non-production environments, it also
 * logs detailed error information to the console.
 *
 * @template T - The Valibot schema type that extends GenericSchema
 * @param schema - The Valibot schema to validate against
 * @param data - The unknown data to validate
 * @param context - Optional context string for better error tracking and debugging
 * @returns {v.InferOutput<T>} The validated and type-safe data as inferred from the schema
 *
 * @example
 * ```typescript
 * const userSchema = object({
 *   id: number(),
 *   name: string(),
 *   email: string()
 * })
 *
 * const userData = { id: 1, name: "John", email: "john@example.com" }
 * const validatedUser = validateData(userSchema, userData, "user registration")
 * // validatedUser is now typed as { id: number, name: string, email: string }
 * ```
 *
 * @throws Will not throw but will log errors and capture exceptions in Sentry
 */
export function validateData<T extends GenericSchema>(
  schema: T,
  data: unknown,
  context?: string,
): v.InferOutput<T> {
  const result = safeParse(schema, data)

  if (!result.success) {
    const title = `Schema validation failed${context ? ` (${context})` : ''}`
    const errorMessage = flatten(result.issues)

    // Log to console in non-production environments
    if (env.MODE !== 'production') {
      console.error(title, {
        context,
        data,
        issues: result.issues,
        errorMessage: JSON.stringify(errorMessage, null, 2),
      })
    }

    // Capture error in Sentry
    captureException(new Error(title), {
      level: 'error',
      extra: {
        context,
        data: safeStringify(data),
        issues: safeStringify(errorMessage),
        schema: safeStringify(schema),
      },
      tags: {
        section: SENTRY_SECTIONS.schemaValidation,
        context: context || 'unknown',
      },
    })

    return data as v.InferOutput<T>
  }

  return result.output
}
